/* ============================================================
   AppErrorBoundary — root-level guard so a single render error
   (corrupted localStorage state after a deploy, stale cached
   chunk, runtime exception during hydration, etc.) never leaves
   the user staring at a blank white page.

   Surface:
   - Friendly fallback explaining what happened in plain language
   - Primary action: "Clear cache & reload" — wipes localStorage,
     sessionStorage, and IndexedDB, then hard-reloads the page.
     This is the same fix a support agent would walk a user
     through; making it one click closes the catastrophic gap.
   - Secondary action: "Reload only" for cases where state is
     fine but a transient runtime error happened.
   - Localized into uk/ru/en using the browser language so the
     copy doesn't switch on the user mid-incident.
   ============================================================ */
import React from 'react';

const COPY = {
  uk: {
    title: 'Щось пішло не так',
    body: 'Сторінка не змогла завантажитись. Це зазвичай зникає після оновлення з очищеним кешем.',
    primary: 'Очистити кеш і перезавантажити',
    secondary: 'Просто перезавантажити',
    details: 'Технічні деталі',
  },
  ru: {
    title: 'Что-то пошло не так',
    body: 'Страница не смогла загрузиться. Обычно это исчезает после обновления с очисткой кэша.',
    primary: 'Очистить кэш и перезагрузить',
    secondary: 'Просто перезагрузить',
    details: 'Технические детали',
  },
  en: {
    title: 'Something went wrong',
    body: "We couldn't finish loading the page. Clearing the cache usually fixes this.",
    primary: 'Clear cache and reload',
    secondary: 'Just reload',
    details: 'Technical details',
  },
};

function pickLanguage() {
  if (typeof navigator === 'undefined') return 'en';
  const value = String(navigator.language || '').toLowerCase();
  if (value.startsWith('uk')) return 'uk';
  if (value.startsWith('ru')) return 'ru';
  return 'en';
}

function clearClientStateAndReload() {
  try { window.localStorage.clear(); } catch { /* private mode */ }
  try { window.sessionStorage.clear(); } catch { /* private mode */ }
  // Best-effort IndexedDB wipe — no-op if not used or not supported.
  try {
    if (window.indexedDB?.databases) {
      window.indexedDB.databases().then(dbs => {
        dbs.forEach(db => { if (db.name) window.indexedDB.deleteDatabase(db.name); });
      }).catch(() => {});
    }
  } catch { /* ignore */ }
  // Cache-busting reload — `location.reload(true)` is deprecated; use the
  // replace trick so the back stack doesn't carry the error page.
  const url = new URL(window.location.href);
  url.searchParams.set('_cb', String(Date.now()));
  window.location.replace(url.toString());
}

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error, info: null };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Ship to console so DevTools / Sentry-style integrations still see it.
    try {
      console.error('[AppErrorBoundary]', error, info?.componentStack);
    } catch { /* ignore */ }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const copy = COPY[pickLanguage()] || COPY.en;
    const details = [
      this.state.error?.name,
      this.state.error?.message,
      this.state.info?.componentStack,
    ].filter(Boolean).join('\n').slice(0, 1800);

    return (
      <div className="app-error-boundary" role="alert" aria-live="assertive">
        <div className="app-error-boundary-card">
          <div className="app-error-boundary-icon" aria-hidden="true">⚠</div>
          <h1>{copy.title}</h1>
          <p>{copy.body}</p>
          <div className="app-error-boundary-actions">
            <button type="button" className="app-error-boundary-primary" onClick={clearClientStateAndReload}>
              {copy.primary}
            </button>
            <button type="button" className="app-error-boundary-secondary" onClick={() => window.location.reload()}>
              {copy.secondary}
            </button>
          </div>
          {details && (
            <details className="app-error-boundary-details">
              <summary>{copy.details}</summary>
              <pre>{details}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
